/**
 * Tuiles des modules d'un dossier (Courtage / Travaux d'optimisation / futurs).
 *
 * Affiche pour chaque module actif : statut, étape actuelle, jauge, prochaine action.
 * Clic sur une tuile → scroll vers la section du module correspondant.
 * Pour un module NON ACTIVÉ : affiche une tuile "Activer ce module".
 */

import { MODULES_CONFIG } from '@/lib/modulesConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, ArrowRight, PlayCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Détecte si le module a une action en RETARD (tâche non terminée avec date passée)
// ou s'il n'a AUCUNE action future planifiée alors qu'il est actif (unsecured).
function isModuleOverdue(moduleInfo) {
    if (!moduleInfo || !moduleInfo.needs) return false;
    // Module actif SANS aucune action → à sécuriser (rouge)
    if (!moduleInfo.has_next_action) return true;
    // Module actif AVEC action dont la date est passée → tâche en retard
    const d = moduleInfo.next_action?.date;
    if (!d) return false;
    try {
        const s2 = String(d).replace('Z', '+00:00');
        const dt = new Date(s2.length === 10 ? s2 + 'T00:00:00+00:00' : s2);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return dt < now;
    } catch { return false; }
}

// Libellés d'étapes techniques → texte lisible (récupère les mêmes que la liste des dossiers)
const STAGE_LABELS_EP = {
    ep_prospection: 'Prospection', ep_veille: 'En veille', ep_devis_envoye: 'Devis envoyé',
    ep_devis_accepte: 'Devis accepté', ep_contrat_signe: 'Contrat signé', ep_termine: 'Terminé',
    ep_valide: 'Validé', ep_gagne: 'Gagné', ep_perdu: 'Perdu',
};
const STAGE_LABELS_CH = {
    ch_devis_envoye: 'Devis envoyé', ch_devis_accepte: 'Devis accepté', ch_devis_signe: 'Devis signé',
    ch_dossier_subvention: 'Dossier subvention', ch_subvention_obtenue: 'Subvention obtenue',
    ch_planification: 'Planification', ch_en_cours: 'Travaux en cours', ch_chantier_en_cours: 'Travaux en cours',
    ch_termine: 'Terminé', ch_chantier_termine: 'Terminé', ch_attente_paiement: 'Attente paiement',
    ch_paiement_effectue: 'Paiement effectué', ch_valide: 'Validé', ch_gagne: 'Gagné', ch_perdu: 'Perdu',
};

const FINAL_STAGES_EP = ['ep_gagne', 'ep_valide', 'ep_termine', 'ep_perdu'];
const FINAL_STAGES_CH = ['ch_gagne', 'ch_valide', 'ch_termine', 'ch_chantier_termine',
    'ch_paiement_effectue', 'ch_perdu'];

function stageLabelFor(moduleKey, stage) {
    if (moduleKey === 'easy_power') return STAGE_LABELS_EP[stage] || stage || '—';
    if (moduleKey === 'chantier') return STAGE_LABELS_CH[stage] || stage || '—';
    return stage || '—';
}

function progressForStage(moduleKey, stage) {
    // Approximation visuelle basée sur les étapes classiques (ordre logique)
    if (!stage) return 0;
    const orderEP = ['ep_prospection', 'ep_veille', 'ep_devis_envoye', 'ep_devis_accepte',
        'ep_contrat_signe', 'ep_termine', 'ep_valide', 'ep_gagne'];
    const orderCH = ['ch_devis_envoye', 'ch_devis_accepte', 'ch_devis_signe',
        'ch_dossier_subvention', 'ch_subvention_obtenue', 'ch_planification',
        'ch_chantier_en_cours', 'ch_en_cours', 'ch_chantier_termine', 'ch_termine',
        'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide', 'ch_gagne'];
    const arr = moduleKey === 'easy_power' ? orderEP : orderCH;
    const idx = arr.indexOf(stage);
    if (idx === -1) return 0;
    return Math.round(((idx + 1) / arr.length) * 100);
}

function isFinalStage(moduleKey, stage) {
    if (moduleKey === 'easy_power') return FINAL_STAGES_EP.includes(stage);
    return FINAL_STAGES_CH.includes(stage);
}

function isLostStage(moduleKey, stage) {
    if (moduleKey === 'easy_power') return stage === 'ep_perdu';
    return stage === 'ch_perdu';
}

/**
 * Tuiles pour chaque module. Rendre `onOpen(moduleKey)` pour la nav vers la section.
 *
 * @param {Object}   prospect        - Prospect courant
 * @param {Function} onOpen          - (moduleKey) => void
 * @param {Function} onActivate      - (moduleKey) => void
 * @param {Object}   tabUnread       - { easy_power: n, chantier: n } messages non lus du mur
 * @param {Object}   nextActionInfo  - Réponse `/prospects/:id/next-action` (per-module)
 */
export default function ProspectModuleTiles({ prospect, onOpen, onActivate, tabUnread, nextActionInfo }) {
    if (!prospect) return null;

    return (
        <div
            className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2"
            data-testid="prospect-module-tiles"
        >
            {MODULES_CONFIG.map((mod) => {
                if (prospect.module_permissions?.[mod.key]?.read === false) return null;
                const stage = prospect[mod.stageField];
                const disabled = prospect[mod.disabledField];
                const isActive = !!stage && !disabled;
                const Icon = mod.icon;
                const label = mod.label;
                const stageLabel = stageLabelFor(mod.key, stage);
                const pct = progressForStage(mod.key, stage);
                const final = isFinalStage(mod.key, stage);
                const lost = isLostStage(mod.key, stage);

                // Tuile INACTIVE
                if (!isActive) {
                    return (
                        <Card
                            key={mod.key}
                            className="border-dashed border-2 border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition"
                            data-testid={`module-tile-${mod.key}-inactive`}
                        >
                            <CardContent className="p-4 sm:p-5 flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5 text-slate-400" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-slate-500 text-sm sm:text-base truncate">{label}</p>
                                    <p className="text-xs text-slate-400">Module non activé</p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onActivate && onActivate(mod.key)}
                                    disabled={prospect.module_permissions?.[mod.key]?.write === false}
                                    data-testid={`module-tile-${mod.key}-activate`}
                                    className="shrink-0"
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Activer
                                </Button>
                            </CardContent>
                        </Card>
                    );
                }

                // Tuile ACTIVE
                const statusColor = lost
                    ? 'text-rose-700 bg-rose-100 border-rose-200'
                    : final
                        ? 'text-emerald-700 bg-emerald-100 border-emerald-200'
                        : 'text-blue-700 bg-blue-100 border-blue-200';
                const statusLabel = lost ? 'Perdu' : final ? 'Terminé' : 'En cours';

                // ─── Compteur "unread" par tuile ───
                // Messages non lus du mur + tâche(s) en retard/à sécuriser.
                // Rouge si retard/unsecured ; violet sinon (messages non lus seuls).
                // Aucune pastille sur les modules Terminés/Perdus (ils ne demandent plus d'action).
                const unreadMsg = (tabUnread && tabUnread[mod.key]) || 0;
                const modInfo = nextActionInfo && nextActionInfo[mod.key];
                const overdue = !final && !lost && isModuleOverdue(modInfo);
                const badgeCount = unreadMsg + (overdue ? 1 : 0);
                const showBadge = !final && !lost && badgeCount > 0;
                const badgeTitle = overdue
                    ? (unreadMsg > 0
                        ? `${unreadMsg} nouveau${unreadMsg > 1 ? 'x' : ''} message${unreadMsg > 1 ? 's' : ''} · Action en retard ou à planifier`
                        : 'Action en retard ou à planifier')
                    : `${unreadMsg} nouveau${unreadMsg > 1 ? 'x' : ''} message${unreadMsg > 1 ? 's' : ''}`;

                return (
                    <button
                        key={mod.key}
                        type="button"
                        onClick={() => onOpen && onOpen(mod.key)}
                        data-testid={`module-tile-${mod.key}`}
                        className={cn(
                            "relative text-left rounded-xl border-2 bg-white p-4 sm:p-5",
                            "transition hover:shadow-md hover:-translate-y-[1px]",
                            lost ? "border-rose-200" : final ? "border-emerald-200" : "border-slate-200 hover:border-slate-300"
                        )}
                    >
                        <div className="flex items-start gap-3">
                            <div className={cn(
                                "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br shadow-sm",
                                mod.accent
                            )}>
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-bold text-slate-900 text-sm sm:text-base truncate">{label}</p>
                                    <span className={cn(
                                        "text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border",
                                        statusColor
                                    )}>
                                        {statusLabel}
                                    </span>
                                </div>
                                <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">
                                    Étape : <b className="text-slate-700">{stageLabel}</b>
                                </p>
                                {/* Progress bar visuelle */}
                                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                    <div
                                        className={cn(
                                            "h-full rounded-full transition-all",
                                            lost ? "bg-rose-400" : final ? "bg-emerald-500" : `bg-gradient-to-r ${mod.accent}`
                                        )}
                                        style={{ width: `${lost ? 100 : pct}%` }}
                                    />
                                </div>
                                <p className="mt-1.5 flex items-center justify-between text-[11px]">
                                    <span className="text-slate-400">{pct}% du parcours</span>
                                    <span className="inline-flex items-center gap-1 text-slate-500 group-hover:text-slate-800">
                                        Ouvrir <ArrowRight className="w-3 h-3" />
                                    </span>
                                </p>
                            </div>
                        </div>
                        {showBadge && (
                            <span
                                data-testid={`module-tile-${mod.key}-notif-badge`}
                                title={badgeTitle}
                                aria-label={badgeTitle}
                                className={cn(
                                    "absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full",
                                    "inline-flex items-center justify-center text-[11px] font-bold leading-none text-white",
                                    "shadow-md ring-2 ring-white",
                                    overdue
                                        ? "bg-rose-600 animate-pulse"
                                        : "bg-purple-600"
                                )}
                            >
                                {badgeCount > 9 ? '9+' : badgeCount}
                            </span>
                        )}
                        {!final && !lost && !showBadge && (
                            <div className="absolute top-2 right-2">
                                <PlayCircle className="w-4 h-4 text-slate-300" />
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
